/* eslint-disable */
/* Code generated by ng-openapi-gen DO NOT EDIT. */

import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';

import { PriceUpdateResponseProducts } from '../models/price-update-response-products';
import { updatePrice } from '../fn/price/update-price';
import { UpdatePrice$Params } from '../fn/price/update-price';


/**
 * Product price synchronization. Accepts HardBit-compatible XML payloads and synchronizes prices into the marketplace.
 */
@Injectable({ providedIn: 'root' })
export class PriceService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `updatePrice()` */
  static readonly UpdatePricePath = '/prices';

  /**
   * Synchronize product prices (HardBit / XML format).
   *
   * Accepts a bulk XML price update payload and synchronizes product prices into the marketplace.
   *
   * **Authentication**
   *
   * Requires a Bearer JWT token in the `Authorization` header. 
   * The token identifies the merchant whose prices are being updated.
   *
   * **Required authority (one of):**
   * - `EASYLOT_ADMIN`
   * - `PRICE_SYNC`
   * - `PRICE_SYNC_ADMIN`
   *
   * **Business preconditions**
   *
   * - A valid PRS configuration must exist for the merchant/site identified by the token.
   * - Each product SKU must have an existing product mapping (SKU → productId) in PRS.
   *   Missing mappings do not cause HTTP-level failure; they produce item-level errors in the response.
   * - Synchronization behavior (e.g. which price types are applied) depends on the merchant/site configuration.
   *
   * **HTTP 200 does not mean full success**
   *
   * The HTTP status reflects request acceptance, not individual product processing outcome. 
   * Always check the `Status` field of each `Product` element in the response XML.
   *
   * **Price type semantics**
   *
   * The `Type` field on each `Price` element determines the price type:
   * - `normal` — the standard selling price
   * - `special` — a promotional/special price (PRS uses this field to identify special prices)
   * - `sale` — additional sale price type (usage depends on merchant configuration)
   *
   * `GroupName` is a pass-through field. PRS does **not** use it to determine whether a price is special. 
   * Use `Type=special` to mark a special price.
   *
   * **Request example:**
   * ```xml
   * <Products>
   *     <Product>
   *         <Action>modify</Action>
   *         <Sku>SKU-001</Sku>
   *         <Prices>
   *             <Vat>27</Vat>
   *             <Price>
   *                 <Type>normal</Type>
   *                 <Net>7874</Net>
   *                 <Gross>9999</Gross>
   *             </Price>
   *             <Price>
   *                 <Type>special</Type>
   *                 <Net>6299</Net>
   *                 <Gross>7999</Gross>
   *             </Price>
   *         </Prices>
   *     </Product>
   * </Products>
   * ```
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updatePrice()` instead.
   *
   * This method sends `application/xml` and handles request body of type `application/xml`.
   */
  updatePrice$Response(params: UpdatePrice$Params, context?: HttpContext): Observable<StrictHttpResponse<PriceUpdateResponseProducts>> {
    const obs = updatePrice(this.http, this.rootUrl, params, context);
    return obs;
  }

  /**
   * Synchronize product prices (HardBit / XML format).
   *
   * Accepts a bulk XML price update payload and synchronizes product prices into the marketplace.
   *
   * **Authentication**
   *
   * Requires a Bearer JWT token in the `Authorization` header. 
   * The token identifies the merchant whose prices are being updated.
   *
   * **Required authority (one of):**
   * - `EASYLOT_ADMIN`
   * - `PRICE_SYNC`
   * - `PRICE_SYNC_ADMIN`
   *
   * **Business preconditions**
   *
   * - A valid PRS configuration must exist for the merchant/site identified by the token.
   * - Each product SKU must have an existing product mapping (SKU → productId) in PRS.
   *   Missing mappings do not cause HTTP-level failure; they produce item-level errors in the response.
   * - Synchronization behavior (e.g. which price types are applied) depends on the merchant/site configuration.
   *
   * **HTTP 200 does not mean full success**
   *
   * The HTTP status reflects request acceptance, not individual product processing outcome. 
   * Always check the `Status` field of each `Product` element in the response XML.
   *
   * **Price type semantics**
   *
   * The `Type` field on each `Price` element determines the price type:
   * - `normal` — the standard selling price
   * - `special` — a promotional/special price (PRS uses this field to identify special prices)
   * - `sale` — additional sale price type (usage depends on merchant configuration)
   *
   * `GroupName` is a pass-through field. PRS does **not** use it to determine whether a price is special. 
   * Use `Type=special` to mark a special price.
   *
   * **Request example:**
   * ```xml
   * <Products>
   *     <Product>
   *         <Action>modify</Action>
   *         <Sku>SKU-001</Sku>
   *         <Prices>
   *             <Vat>27</Vat>
   *             <Price>
   *                 <Type>normal</Type>
   *                 <Net>7874</Net>
   *                 <Gross>9999</Gross>
   *             </Price>
   *             <Price>
   *                 <Type>special</Type>
   *                 <Net>6299</Net>
   *                 <Gross>7999</Gross>
   *             </Price>
   *         </Prices>
   *     </Product>
   * </Products>
   * ```
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updatePrice$Response()` instead.
   *
   * This method sends `application/xml` and handles request body of type `application/xml`.
   */
  updatePrice(params: UpdatePrice$Params, context?: HttpContext): Observable<PriceUpdateResponseProducts> {
    const resp = this.updatePrice$Response(params, context);
    return resp.pipe(
      map((r: StrictHttpResponse<PriceUpdateResponseProducts>): PriceUpdateResponseProducts => r.body)
    );
  }

}
